import React, { useEffect, useState } from 'react';
import {
    Box, Grid, Typography, Card, CardContent, ButtonBase, Button, Table, TableBody,
    TableCell, TableHead, TableRow, Paper, TablePagination, TextField
} from '@mui/material';
import { useNavigate } from 'react-router';
import Sidebar from './Sidebar';
import axios from '../axiosConfig';

const Reports = () => {
    const navigate = useNavigate();

    // Состояние для свернутости сайдбара
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Состояния для данных о ликвидности
    const [kgsLiquidity, setKgsLiquidity] = useState(null);
    const [usdtLiquidity, setUsdtLiquidity] = useState(null);
    const [usdLiquidity, setUsdLiquidity] = useState(null);

    // Состояния для выбранного ассета и его детализированных данных
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [assetData, setAssetData] = useState(null);

    // Входящие транзакции и пагинация
    const [incomingData, setIncomingData] = useState([]);
    const [incomingPage, setIncomingPage] = useState(0);
    const [incomingRowsPerPage, setIncomingRowsPerPage] = useState(10);
    const [incomingTotalCount, setIncomingTotalCount] = useState(0);

    // Исходящие транзакции и пагинация
    const [outgoingData, setOutgoingData] = useState([]);
    const [outgoingPage, setOutgoingPage] = useState(0);
    const [outgoingRowsPerPage, setOutgoingRowsPerPage] = useState(10);
    const [outgoingTotalCount, setOutgoingTotalCount] = useState(0);

    // Состояние для ввода суммы и средней цены
    const [amount, setAmount] = useState('');
    const [averagePrice, setAveragePrice] = useState(''); // Для USDT

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (!token || token === 'null') {
            navigate('/login');
        } else {
            fetchLiquidityData(); // Получаем начальные данные
        }
    }, [navigate]);

    const handleAuthError = (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('authToken'); // Удаляем токен
            navigate('/login'); // Перенаправляем на страницу входа
        }
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const fetchLiquidityData = () => {
        axios.get('/liquidity/liquidity', { params: { asset: 'KGS' } })
            .then((response) => setKgsLiquidity(response.data))
            .catch((error) => {
                console.error('Ошибка при получении данных KGS:', error);
                handleAuthError(error); // Обрабатываем ошибку 401
            });

        axios.get('/liquidity/liquidity', { params: { asset: 'USDT' } })
            .then((response) => setUsdtLiquidity(response.data))
            .catch((error) => {
                console.error('Ошибка при получении данных USDT:', error);
                handleAuthError(error); // Обрабатываем ошибку 401
            });

        axios.get('/liquidity/liquidity', { params: { asset: 'USD' } })
            .then((response) => setUsdLiquidity(response.data))
            .catch((error) => {
                console.error('Ошибка при получении данных USD:', error);
                handleAuthError(error); // Обрабатываем ошибку 401
            });
    };

    // Обработка клика на карточку ассета
    const handleCardClick = (asset) => {
        setSelectedAsset(asset);
        setIncomingPage(0);
        setOutgoingPage(0);
        setAveragePrice(''); // Сбрасываем среднюю цену

        fetchAssetData(asset); // Получаем детализированные данные
        fetchIncomingData(asset.toLowerCase(), 0, incomingRowsPerPage);
        fetchOutgoingData(asset.toLowerCase(), 0, outgoingRowsPerPage);
    };

    const fetchAssetData = (asset) => {
        const lowerCaseAsset = asset.toLowerCase();
        axios.get(`/liquidity/${lowerCaseAsset}`)
            .then((response) => setAssetData(response.data))
            .catch((error) => {
                console.error(`Ошибка при получении данных для ${asset}:`, error);
                handleAuthError(error); // Обрабатываем ошибку 401
            });
    };

    // Функция для форматирования чисел
    const formatNumber = (num) => {
        return num !== undefined && num !== null ? Number(num).toFixed(2) : 'Загрузка...';
    };

    // Функция для форматирования даты
    const formatDate = (timestamp) => {
        if (!timestamp) return 'Нет данных';
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
    };

    const fetchIncomingData = (asset, pageNumber, pageSize) => {
        axios.get(`/liquidity/${asset}/incoming`, { params: { page_number: pageNumber + 1, page_size: pageSize } })
            .then((response) => {
                const data = response.data.list || [];
                setIncomingData(Array.isArray(data) ? data : []);
                const totalItems = (response.data.page_amount || 1) * pageSize;
                setIncomingTotalCount(totalItems);
            })
            .catch((error) => {
                console.error(`Ошибка при получении входящих данных для ${asset}:`, error);
                handleAuthError(error); // Обрабатываем ошибку 401
            });
    };

    const fetchOutgoingData = (asset, pageNumber, pageSize) => {
        axios.get(`/liquidity/${asset}/outgoing`, { params: { page_number: pageNumber + 1, page_size: pageSize } })
            .then((response) => {
                const data = response.data.list || [];
                setOutgoingData(Array.isArray(data) ? data : []);
                const totalItems = (response.data.page_amount || 1) * pageSize;
                setOutgoingTotalCount(totalItems);
            })
            .catch((error) => {
                console.error(`Ошибка при получении исходящих данных для ${asset}:`, error);
                handleAuthError(error); // Обрабатываем ошибку 401
            });
    };

    // Функция для добавления ликвидности с проверкой средней цены для USDT
    const addLiquidity = (asset) => {
        const upperCaseAsset = asset.toUpperCase();
        const payload = {
            asset: upperCaseAsset,
            amount: Number(amount)
        };

        if (upperCaseAsset === 'USDT') {
            if (!averagePrice) {
                alert('Средняя цена для USDT обязательна.');
                return;
            }
            payload.avg_price = Number(averagePrice);
        }

        axios.post(`/liquidity/add`, payload)
            .then(() => {
                setAmount('');
                setAveragePrice('');
                fetchAssetData(upperCaseAsset.toLowerCase());
                fetchIncomingData(upperCaseAsset.toLowerCase(), incomingPage, incomingRowsPerPage);
                fetchOutgoingData(upperCaseAsset.toLowerCase(), outgoingPage, outgoingRowsPerPage);
            })
            .catch((error) => {
                console.error('Ошибка при добавлении ликвидности:', error);
                handleAuthError(error); // Обрабатываем ошибку 401
            });
    };

    // Функция для вычитания ликвидности
    const subtractLiquidity = (asset) => {
        const upperCaseAsset = asset.toUpperCase();
        const payload = {
            asset: upperCaseAsset,
            amount: Number(amount)
        };

        if (upperCaseAsset === 'USDT') {
            if (!averagePrice) {
                alert('Средняя цена для USDT обязательна.');
                return;
            }
            payload.avg_price = Number(averagePrice);
        }
        axios.post(`/liquidity/subtract`, payload)
            .then(() => {
                setAmount('');
                fetchAssetData(upperCaseAsset.toLowerCase());
                fetchIncomingData(upperCaseAsset.toLowerCase(), incomingPage, incomingRowsPerPage);
                fetchOutgoingData(upperCaseAsset.toLowerCase(), outgoingPage, outgoingRowsPerPage);
            })
            .catch((error) => {
                console.error('Ошибка при вычитании ликвидности:', error);
                handleAuthError(error); // Обрабатываем ошибку 401
            });
    };

    const handleBackClick = () => {
        setSelectedAsset(null);
        setAssetData(null);
        setIncomingData([]);
        setOutgoingData([]);
    };

    const generateTableHeaders = (data) => {
        if (!Array.isArray(data) || data.length === 0) return [];
        return Object.keys(data[0]).filter((key) => key !== 'pk' && key !== 'sk');
    };

    const renderTable = (data) => (
        <div style={{ overflowX: 'auto' }}>
            <Table>
                <TableHead>
                    <TableRow>
                        {generateTableHeaders(data).map((header) => (
                            <TableCell key={header}>{header}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row, index) => (
                        <TableRow key={index}>
                            {generateTableHeaders(data).map((key) => (
                                <TableCell key={key}>
                                    {key === 'datetime' ? formatDate(row[key]) :
                                        typeof row[key] === 'object' ? JSON.stringify(row[key]) : row[key]}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );

    const handleIncomingPageChange = (event, newPage) => {
        setIncomingPage(newPage);
        const lowerCaseAsset = selectedAsset.toLowerCase();
        fetchIncomingData(lowerCaseAsset, newPage, incomingRowsPerPage);
    };

    const handleIncomingRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setIncomingRowsPerPage(newRowsPerPage);
        setIncomingPage(0);
        const lowerCaseAsset = selectedAsset.toLowerCase();
        fetchIncomingData(lowerCaseAsset, 0, newRowsPerPage);
    };

    const handleOutgoingPageChange = (event, newPage) => {
        setOutgoingPage(newPage);
        const lowerCaseAsset = selectedAsset.toLowerCase();
        fetchOutgoingData(lowerCaseAsset, newPage, outgoingRowsPerPage);
    };

    const handleOutgoingRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setOutgoingRowsPerPage(newRowsPerPage);
        setOutgoingPage(0);
        const lowerCaseAsset = selectedAsset.toLowerCase();
        fetchOutgoingData(lowerCaseAsset, 0, newRowsPerPage);
    };

    const cardStyle = {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    };

    const cardContentStyle = {
        flexGrow: 1,
    };

    return (
        <Box display="flex">
            {/* Сайдбар */}
            <Sidebar isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />

            {/* Контент */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    marginLeft: isCollapsed ? '80px' : '250px', // Отступ контента в зависимости от состояния сайдбара
                    transition: 'margin-left 0.3s ease',
                    padding: '20px',
                    width: `calc(100% - ${isCollapsed ? '80px' : '250px'})`, // Установка ширины в зависимости от состояния сайдбара
                }}
            >
                {/* Отображаем карточки ассетов, если ассет не выбран */}
                {!selectedAsset && (
                    <Grid container spacing={2} alignItems="stretch">
                        <Grid item xs={4}>
                            <ButtonBase onClick={() => handleCardClick('KGS')} style={{ width: '100%', height: '100%' }}>
                                <Card style={cardStyle}>
                                    <CardContent style={cardContentStyle}>
                                        <Typography variant="h5" gutterBottom>KGS</Typography>
                                        <Typography>
                                            Свободный баланс: {formatNumber(kgsLiquidity?.free_balance)}
                                        </Typography>
                                        {kgsLiquidity?.commissions && (
                                            <Typography>
                                                Комиссии: {formatNumber(kgsLiquidity.commissions)}
                                            </Typography>
                                        )}
                                        {kgsLiquidity?.profits && (
                                            <Typography>
                                                Прибыль: {formatNumber(kgsLiquidity.profits)}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </ButtonBase>
                        </Grid>

                        <Grid item xs={4}>
                            <ButtonBase onClick={() => handleCardClick('USDT')} style={{ width: '100%', height: '100%' }}>
                                <Card style={cardStyle}>
                                    <CardContent style={cardContentStyle}>
                                        <Typography variant="h5" gutterBottom>USDT</Typography>
                                        <Typography>
                                            Свободный баланс: {formatNumber(usdtLiquidity?.free_balance)}
                                        </Typography>
                                        {usdtLiquidity?.commissions && (
                                            <Typography>
                                                Комиссии: {formatNumber(usdtLiquidity.commissions)}
                                            </Typography>
                                        )}
                                        {usdtLiquidity?.profits && (
                                            <Typography>
                                                Прибыль: {formatNumber(usdtLiquidity.profits)}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </ButtonBase>
                        </Grid>

                        <Grid item xs={4}>
                            <ButtonBase onClick={() => handleCardClick('USD')} style={{ width: '100%', height: '100%' }}>
                                <Card style={cardStyle}>
                                    <CardContent style={cardContentStyle}>
                                        <Typography variant="h5" gutterBottom>USD</Typography>
                                        <Typography>
                                            Свободный баланс: {formatNumber(usdLiquidity?.free_balance)}
                                        </Typography>
                                        {usdLiquidity?.commissions && (
                                            <Typography>
                                                Комиссии: {formatNumber(usdLiquidity.commissions)}
                                            </Typography>
                                        )}
                                        {usdLiquidity?.profits && (
                                            <Typography>
                                                Прибыль: {formatNumber(usdLiquidity.profits)}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </ButtonBase>
                        </Grid>
                    </Grid>
                )}

                {/* Форма для добавления/вычитания ликвидности */}
                {selectedAsset && (
                    <Box mb={2} mt={2}>
                        <Button variant="contained" onClick={handleBackClick}>Назад</Button>
                        <br />
                        <br />
                        <TextField
                            label="Сумма"
                            variant="outlined"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            type="number"
                            fullWidth
                        />

                        {/* Поле для ввода средней цены, если выбран USDT */}
                        {selectedAsset === 'USDT' && (
                            <TextField
                                label="Средняя цена"
                                variant="outlined"
                                value={averagePrice}
                                onChange={(e) => setAveragePrice(e.target.value)}
                                type="number"
                                fullWidth
                                margin="normal"
                            />
                        )}

                        <Box mt={1}>
                            <Button variant="contained" color="primary" onClick={() => addLiquidity(selectedAsset)}>
                                Добавить
                            </Button>
                            <Button variant="contained" color="secondary" onClick={() => subtractLiquidity(selectedAsset)} style={{ marginLeft: '10px' }}>
                                Вычесть
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Отображаем детализированные данные, если ассет выбран */}
                {selectedAsset && (
                    <Box mt={4}>
                        <Typography variant="h4" gutterBottom>Детализированные данные для {selectedAsset}</Typography>
                        <Typography variant="h6">Основные данные</Typography>
                        {assetData ? (
                            <Paper>
                                <Table>
                                    <TableBody>
                                        {Object.keys(assetData).map((key) => (
                                            <TableRow key={key}>
                                                <TableCell>{key}</TableCell>
                                                <TableCell>
                                                    {typeof assetData[key] === 'object' ? JSON.stringify(assetData[key]) : assetData[key]}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        ) : (
                            <Typography>Загрузка данных...</Typography>
                        )}

                        {/* Таблица входящих транзакций */}
                        <Typography variant="h6" mt={4}>Входящие транзакции</Typography>
                        <Paper>
                            {incomingData.length > 0 ? (
                                <>
                                    {renderTable(incomingData)}
                                    <TablePagination
                                        component="div"
                                        count={incomingTotalCount}
                                        page={incomingPage}
                                        onPageChange={handleIncomingPageChange}
                                        rowsPerPage={incomingRowsPerPage}
                                        onRowsPerPageChange={handleIncomingRowsPerPageChange}
                                        labelRowsPerPage="Строк на странице:"
                                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                                    />
                                </>
                            ) : (
                                <Typography>Нет входящих транзакций</Typography>
                            )}
                        </Paper>

                        {/* Таблица исходящих транзакций */}
                        <Typography variant="h6" mt={4}>Исходящие транзакции</Typography>
                        <Paper>
                            {outgoingData.length > 0 ? (
                                <>
                                    {renderTable(outgoingData)}
                                    <TablePagination
                                        component="div"
                                        count={outgoingTotalCount}
                                        page={outgoingPage}
                                        onPageChange={handleOutgoingPageChange}
                                        rowsPerPage={outgoingRowsPerPage}
                                        onRowsPerPageChange={handleOutgoingRowsPerPageChange}
                                        labelRowsPerPage="Строк на странице:"
                                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                                    />
                                </>
                            ) : (
                                <Typography>Нет исходящих транзакций</Typography>
                            )}
                        </Paper>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Reports;
